import React from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import Flex from '@wix/da-ds/pkg/Flex';
import MediaIndicator from '@wix/da-ds/pkg/thumbs/MediaIndicator';
import BoostedIndicator from '@wix/da-ds/pkg/thumbs/BoostedIndicator';
import Stats from '../../Stats';
import DeviationLink from '../../Duperbrowse/DeviationLink';
import {
  ExtraBiDataForDeviationLink,
  ExtraBiDataForTierLink,
} from '../../Duperbrowse/DeviationLink/useBiDataForDeviationLink';
import DeviationThumbIndicators, {
  AllowedIndicators,
} from '../../DeviationViews/Thumb/legos/DeviationThumbIndicators';
import {
  getVideoDuration,
  isVideoOrGif,
  getDeviationPrice,
} from '../../utils/deviations';
import { formatDollars } from '../../utils/formatting';
import {
  isBoostedDeviation,
  isMultiImageDeviation,
} from '../../DeviationViews/_helpers';
import PriceWithPoints from '../_partials/PriceWithPoints';
import ShopCardDumb from '../_partials/ShopCardDumb';

import s from './DeviationShopCard.scss';

export interface Props {
  deviation: PapiDeviation;
  width: number;
  withAuthorDetails?: boolean;
  allowedIndicators?: AllowedIndicators;
  withStats?: boolean;
  withPremiumDownloadCount?: boolean;
  withDreamupLabel?: boolean;
  withDescriptionBlock?: boolean;
  detailsRightSlot?: React.ReactNode;
  belowCoverImageRightSlot?: React.ReactNode;
  footerSlot?: React.ReactNode;
  noCentsOnPrice?: boolean;
  overrideHref?: string;
  withOwnerDetails?: boolean;
  extraBiData?: ExtraBiDataForDeviationLink | ExtraBiDataForTierLink;
}

const DeviationShopCard: React.FC<Props> = ({
  deviation,
  width,
  withAuthorDetails,
  allowedIndicators = AllowedIndicators.All,
  withStats,
  withPremiumDownloadCount,
  withDreamupLabel,
  withDescriptionBlock,
  detailsRightSlot,
  belowCoverImageRightSlot,
  footerSlot,
  noCentsOnPrice,
  overrideHref,
  withOwnerDetails,
  extraBiData,
}) => {
  const { t } = useTranslation();
  const { dollarsPrice, pointsPrice } = getDeviationPrice(deviation);
  const isBoosted = isBoostedDeviation(deviation);
  const owner = deviation.adoptable?.owner;

  const formattedDollarsPrice = noCentsOnPrice
    ? `$${parseInt(dollarsPrice)}`
    : formatDollars(dollarsPrice);

  return (
    <div className={s['root']}>
      <DeviationLink
        className={s['link']}
        deviation={deviation}
        overrideHref={overrideHref}
        extraBiData={extraBiData}
        canLinkToStash
        aria-label={t('shop.thumb.a11y.shopLink', {
          username: owner?.username ?? deviation.author?.username,
          deviationName: deviation.title,
          dollarsPrice: formattedDollarsPrice,
          count: pointsPrice,
        })}
      />
      <ShopCardDumb
        // Hiding the rest from screenreaders because the link's aria-label has the necessary info
        aria-hidden="true"
        width={width}
        coverImage={deviation}
        title={deviation.title}
        isBoosted={isBoosted}
        withAuthorDetails={withAuthorDetails}
        author={deviation.author}
        withDescriptionBlock={withDescriptionBlock}
        description={deviation.textContent}
        withOwnerDetails={withOwnerDetails}
        isMultiImage={isMultiImageDeviation(deviation)}
        owner={owner}
        indicatorsSlot={
          <>
            {allowedIndicators !== AllowedIndicators.None && (
              <DeviationThumbIndicators
                className={s['indicators']}
                deviation={deviation}
                allowedIndicators={allowedIndicators}
              />
            )}
            <div className={s['indicators-container']}>
              {isBoosted && isMultiImageDeviation(deviation) && (
                <BoostedIndicator className={s['boosted']} />
              )}
              <MediaIndicator
                className={s['media-indicator']}
                type={isVideoOrGif(deviation)}
                duration={getVideoDuration(deviation)}
                isMultiImage={isMultiImageDeviation(deviation)}
              />
            </div>
          </>
        }
        belowCoverImageSlot={
          withStats && (
            <Flex justifyContent="space-between">
              <Stats
                deviation={deviation}
                withViewCount={false}
                withBadges={false}
                withCommentControl={false}
                withSubscriberCount={false}
                withPremiumDownloadCount={withPremiumDownloadCount}
                withUserList={false}
                withDaProtectIndicator={false}
                withNoAiIndicator={false}
                onlyNumbers
              />
              {belowCoverImageRightSlot}
            </Flex>
          )
        }
        detailsRightSlot={
          detailsRightSlot || (
            <PriceWithPoints
              formattedDollarsPrice={formattedDollarsPrice}
              pointsPrice={pointsPrice}
            />
          )
        }
        footerSlot={footerSlot}
      />
    </div>
  );
};
DeviationShopCard.displayName = 'DeviationShopCard';

export default DeviationShopCard;
